import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Target, TrendingUp, Building2, Wallet, Banknote, CheckCircle2, Circle,
  RefreshCw, Loader2, Layers, CalendarClock, Eye,
} from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(Number(n || 0));

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-nardo-200 bg-white p-5 relative overflow-hidden" data-testid={`kpi-${label}`}>
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-nardo-500 text-[11px] uppercase tracking-[0.2em] mb-2">
        <Icon className="w-4 h-4" style={{ color: accent }} /> {label}
      </div>
      <div className="text-3xl font-display font-bold text-navy leading-none">{value}</div>
      {sub && <div className="text-xs text-nardo-500 mt-1.5">{sub}</div>}
    </div>
  );
}

export default function OwnerObjectives() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rev, ds] = await Promise.all([
        api.get("/admin/revenue"),
        api.get("/admin/demo-stats").catch(() => ({ data: null })),
      ]);
      setData(rev.data);
      setDemo(ds.data);
    } catch { toast.error("No se pudo cargar el panel de objetivos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "owner") load(); }, [user]); // eslint-disable-line

  const t = data?.totals || {};
  const schools = data?.schools || [];
  const milestones = data?.objectives || [1, 3, 5, 8, 10];
  const reached = t.active_count || 0;
  const nextTarget = useMemo(() => milestones.find((m) => reached < m) || milestones[milestones.length - 1], [milestones, reached]);
  const prevTarget = useMemo(() => [...milestones].reverse().find((m) => reached >= m) || 0, [milestones, reached]);
  const progressPct = Math.min(100, Math.round((reached / (milestones[milestones.length - 1] || 1)) * 100));

  const planName = (k) => (data?.plans || []).find((p) => p.key === k)?.name || "Sin plan";

  if (user && user.role !== "owner") {
    return <div className="text-center py-16 text-nardo-500">Acceso solo para el propietario.</div>;
  }

  return (
    <div data-testid="owner-objectives">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-navy flex items-center gap-2">
          <Target className="w-6 h-6 text-navy" /> Objetivos de venta e ingresos
        </h1>
        <button onClick={load} data-testid="objectives-refresh" className="inline-flex items-center gap-1.5 text-xs text-navy border border-navy/30 hover:bg-navy hover:text-white px-3 py-2 rounded-md transition-colors">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Actualizar
        </button>
      </div>
      <p className="text-sm text-nardo-500 mb-6">Control en vivo de cada colegio registrado, su plan, el ingreso mensual recurrente y el pago inicial. Se actualiza solo al asignar planes.</p>

      {loading && !data ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-navy" /></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi icon={TrendingUp} label="Ingreso mensual" value={fmt(t.monthly_expected)} sub={`${t.active_count || 0} colegio(s) activo(s) · recurrente`} accent="#10b981" />
            <Kpi icon={Banknote} label="Pago inicial total" value={fmt(t.initial_total)} sub="Suma de implementación de todos los planes" accent="#f5c518" />
            <Kpi icon={Wallet} label="Proyección anual" value={fmt((t.monthly_expected || 0) * 12)} sub="Mensual recurrente × 12 meses" accent="#3b62a8" />
            <Kpi icon={Building2} label="Colegios" value={`${t.active_count || 0}/${t.count || 0}`} sub={`${t.with_plan || 0} con plan asignado`} accent="#9d174d" />
          </div>

          {/* Combinado */}
          <div className="rounded-xl bg-navy text-white p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="objectives-combined">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-nardo-300">Ingreso total a recibir (mensual + pago inicial)</div>
              <div className="text-4xl font-display font-bold mt-1">{fmt((t.monthly_expected || 0) + (t.initial_total || 0))}</div>
            </div>
            <div className="text-right text-xs text-nardo-300">
              <div>Mensual recurrente: <span className="text-white font-semibold">{fmt(t.monthly_expected)}</span></div>
              <div className="mt-1">Pago inicial acumulado: <span className="text-white font-semibold">{fmt(t.initial_total)}</span></div>
            </div>
          </div>

          {/* Objetivos / metas */}
          <div className="rounded-xl border border-nardo-200 bg-white p-5 mb-8" data-testid="objectives-milestones">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-navy" />
              <h2 className="font-display font-semibold text-lg text-navy">Metas de colegios</h2>
              <span className="ml-auto text-xs text-nardo-500">{reached} de {milestones[milestones.length - 1]} · {progressPct}%</span>
            </div>
            <div className="relative h-2 bg-nardo-100 rounded-full mb-6">
              <div className="absolute left-0 top-0 h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="grid grid-cols-5 gap-2">
              {milestones.map((m) => {
                const done = reached >= m;
                return (
                  <div key={m} data-testid={`milestone-${m}`} className={`rounded-lg border p-3 text-center ${done ? "border-emerald-300 bg-emerald-50" : m === nextTarget ? "border-navy/40 bg-navy/5" : "border-nardo-200 bg-nardo-50"}`}>
                    {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" /> : <Circle className="w-5 h-5 text-nardo-400 mx-auto" />}
                    <div className="text-xl font-display font-bold text-navy mt-1">{m}</div>
                    <div className="text-[10px] uppercase tracking-wider text-nardo-500">colegio{m > 1 ? "s" : ""}</div>
                    {m === nextTarget && !done && <div className="text-[10px] text-navy mt-1 font-medium">Faltan {m - reached}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visitas a la demostración */}
          {demo && (
            <div className="rounded-xl border border-nardo-200 bg-white p-5 mb-8" data-testid="demo-stats">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Eye className="w-4 h-4 text-amber-500" />
                <h2 className="font-display font-semibold text-lg text-navy">Visitas a la demostración</h2>
                <span className="ml-auto text-3xl font-display font-bold text-navy" data-testid="demo-total">{demo.total || 0}</span>
                <span className="text-xs text-nardo-500">veces abierta</span>
              </div>
              {(demo.total || 0) === 0 ? (
                <p className="text-sm text-nardo-500">Aún nadie ha abierto la demostración. Comparte el enlace para que tus prospectos la prueben.</p>
              ) : (
                <div className="space-y-2.5">
                  {demo.portals.map((p, i) => {
                    const max = demo.portals[0]?.count || 1;
                    const pct = Math.round((p.count / max) * 100);
                    return (
                      <div key={p.key} data-testid={`demo-portal-stat-${p.key}`}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className={`${i === 0 ? "text-navy font-semibold" : "text-nardo-600"}`}>{p.label}{i === 0 && p.count > 0 ? " · más visto" : ""}</span>
                          <span className="text-nardo-500 font-medium">{p.count}</span>
                        </div>
                        <div className="h-2 bg-nardo-100 rounded-full overflow-hidden">
                          <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: i === 0 ? "#f5c518" : "#3b62a8" }} />
                        </div>
                      </div>
                    );
                  })}
                  {demo.last_visit && <p className="text-[11px] text-nardo-400 pt-1">Última visita: {new Date(demo.last_visit).toLocaleString("es-MX")}</p>}
                </div>
              )}
            </div>
          )}

          {/* Desglose por plan */}
          {data?.by_plan && Object.keys(data.by_plan).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="objectives-byplan">
              <span className="text-[11px] uppercase tracking-wider text-nardo-500 inline-flex items-center gap-1 pt-1"><Layers className="w-3.5 h-3.5" /> Planes vendidos:</span>
              {Object.entries(data.by_plan).map(([k, n]) => (
                <span key={k} className="text-xs bg-navy/5 text-navy px-2.5 py-1 rounded-full">{planName(k)} · {n}</span>
              ))}
            </div>
          )}

          {/* Tabla de colegios */}
          <div className="rounded-xl border border-nardo-200 bg-white overflow-hidden" data-testid="objectives-table">
            <div className="px-5 py-4 border-b border-nardo-200 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-navy" />
              <h2 className="font-display font-semibold text-lg text-navy">Colegios registrados</h2>
              <span className="ml-auto text-xs text-nardo-500">{schools.length} en total</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-nardo-50 text-[10px] uppercase tracking-[0.15em] text-nardo-500">
                    <th className="text-left px-5 py-3 font-medium">Escuela</th>
                    <th className="text-left px-4 py-3 font-medium">Plan vendido</th>
                    <th className="text-right px-4 py-3 font-medium">Pago inicial</th>
                    <th className="text-right px-4 py-3 font-medium">Cobro mensual</th>
                    <th className="text-left px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-nardo-100">
                  {schools.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-nardo-500">Aún no hay colegios registrados.</td></tr>
                  )}
                  {schools.map((s) => (
                    <tr key={s.user_id} data-testid={`school-row-${s.user_id}`} className="hover:bg-nardo-50/60">
                      <td className="px-5 py-3">
                        <div className="font-medium text-navy">{s.school_name}</div>
                        <div className="text-[11px] text-nardo-500">{s.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {s.plan ? <span className="text-xs bg-navy/5 text-navy px-2 py-1 rounded-full">{s.plan_name}</span> : <span className="text-xs text-nardo-400">Sin plan</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-navy">{s.plan ? fmt(s.initial) : "—"}</td>
                      <td className="px-4 py-3 text-right font-medium text-navy">{s.plan ? fmt(s.monthly) : "—"}</td>
                      <td className="px-4 py-3">
                        {s.suspended ? <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Suspendido</span>
                          : s.active ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Activo</span>
                          : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Vencido</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {schools.length > 0 && (
                  <tfoot>
                    <tr className="bg-navy text-white font-display font-semibold">
                      <td className="px-5 py-4" colSpan={2}>TOTALES</td>
                      <td className="px-4 py-4 text-right" data-testid="total-initial">{fmt(t.initial_total)}</td>
                      <td className="px-4 py-4 text-right" data-testid="total-monthly">{fmt(t.monthly_all)}</td>
                      <td className="px-4 py-4 text-[11px] font-normal text-nardo-300">Recurrente activo: {fmt(t.monthly_expected)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
